import { verifiedUsefulInfo } from "./usefulInfoVerifiedFacts";
import { useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { saveBytesToUserDownloads } from "../utils/nativeDownloads";
import { buildUsefulInfoPdf, type UsefulInfoPdfItem } from "../utils/usefulInfoPdf";
import { essentialUsefulInfoText, usefulInfoMapLocations, usefulInfoMapsLabel } from "./usefulInfoPresentation";
import { formatDestinationTime, resolveDestinationTimeZone } from "./destinationTimeZones";
import type { UsefulInfoLayout } from "../types/domain";
import { useAuth } from "../auth/AuthProvider";
import { usePlan } from "../billing/PlanProvider";
import { askTripAI } from "../ai/aiService";
import { useI18n } from "../i18n/I18nProvider";
import {
  createEmptyUsefulInfo,
  listBoardingPasses,
  listTripHotels,
  listTripItinerary,
  listTripPlaces,
  loadTripUsefulInfo,
  saveTripUsefulInfo,
  usefulInfoFieldIds,
  type TripSummary,
  type TripUsefulInfo,
  type UsefulInfoFieldId
} from "./tripStore";
import {
  buildUsefulInfoCountryContext,
  COUNTRY_FACTS_DATASET_VERSION,
  isUsefulInfoNotRequired,
  USEFUL_INFO_NOT_REQUIRED,
  type UsefulInfoCountryContext
} from "./usefulInfoCountryFacts";
import { fetchUsefulInfoEmergencyNumbers, fetchUsefulInfoExchangeRate, fetchUsefulInfoTravelRequirements } from "./usefulInfoDynamicFacts";
import {
  groupUsefulInfoField, uniqueUsefulInfoDestinations,
  type UsefulInfoDestination, type UsefulInfoLocationFact, type UsefulInfoDestinationCandidate
} from "./usefulInfoMultiDestination";

type TranslationKey = Parameters<ReturnType<typeof useI18n>["t"]>[0];

type UsefulInfoGroup = {
  key: TranslationKey;
  description: TranslationKey;
  icon: string;
  fields: Array<{ id: UsefulInfoFieldId; label: TranslationKey; icon: string }>;
};

const groups: UsefulInfoGroup[] = [
  {
    key: "usefulInfo.group.basics",
    description: "usefulInfo.group.basicsDesc",
    icon: "🧭",
    fields: [
      { id: "currency", label: "usefulInfo.field.currency", icon: "💰" },
      { id: "plugType", label: "usefulInfo.field.plugType", icon: "🔌" },
      { id: "drinkingWater", label: "usefulInfo.field.drinkingWater", icon: "💧" },

    ]
  },
  {
    key: "usefulInfo.group.assistance",
    description: "usefulInfo.group.assistanceDesc",
    icon: "☎️",
    fields: [
      { id: "emergencyNumber", label: "usefulInfo.field.emergencyNumber", icon: "🚨" },
      { id: "embassy", label: "usefulInfo.field.embassy", icon: "🏛️" },
      { id: "consulates", label: "usefulInfo.field.consulates", icon: "📍" }
    ]
  },
  {
    key: "usefulInfo.group.entryHealth",
    description: "usefulInfo.group.entryHealthDesc",
    icon: "🛂",
    fields: [
      { id: "passport", label: "usefulInfo.field.passport", icon: "🧳" },
      { id: "visa", label: "usefulInfo.field.visa", icon: "🪪" },
      { id: "requiredVaccines", label: "usefulInfo.field.requiredVaccines", icon: "💉" },
      { id: "healthInsurance", label: "usefulInfo.field.healthInsurance", icon: "🩺" }
    ]
  },
  {
    key: "usefulInfo.group.culture",
    description: "usefulInfo.group.cultureDesc",
    icon: "🌍",
    fields: [
      { id: "governmentType", label: "usefulInfo.field.governmentType", icon: "🏛️" },
      { id: "religion", label: "usefulInfo.field.religion", icon: "🕊️" },
      { id: "publicAffection", label: "usefulInfo.field.publicAffection", icon: "🤝" },
      { id: "lgbtq", label: "usefulInfo.field.lgbtq", icon: "🏳️‍🌈" },
      { id: "civilRights", label: "usefulInfo.field.civilRights", icon: "⚖️" },
      { id: "specialPrecautions", label: "usefulInfo.field.specialPrecautions", icon: "⚠️" }
    ]
  },
  {
    key: "usefulInfo.group.driving",
    description: "usefulInfo.group.drivingDesc",
    icon: "🚗",
    fields: [
      { id: "drivingSide", label: "usefulInfo.field.drivingSide", icon: "🛣️" },
      { id: "internationalDrivingPermit", label: "usefulInfo.field.internationalDrivingPermit", icon: "🪪" }
    ]
  },
  {
    key: "usefulInfo.group.services",
    description: "usefulInfo.group.servicesDesc",
    icon: "📲",
    fields: [
      { id: "taxiApps", label: "usefulInfo.field.taxiApps", icon: "🚕" },
      { id: "foodDeliveryApps", label: "usefulInfo.field.foodDeliveryApps", icon: "🛵" }
    ]
  }
];

const plugTypes = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O"] as const;
type PlugTypeCode = (typeof plugTypes)[number];

const shortFieldIds = new Set<UsefulInfoFieldId>([
  "drinkingWater",
  "taxiApps",
  "foodDeliveryApps",
  "emergencyNumber",
  "governmentType",
  "religion",
  "drivingSide"
]);

const countryFactFieldIds: UsefulInfoFieldId[] = ["plugType", "currency", "drivingSide", "drinkingWater", "taxiApps", "foodDeliveryApps", "emergencyNumber", "governmentType", "religion", "publicAffection", "lgbtq", "civilRights", "specialPrecautions"];
const expandableInfoFields = new Set<UsefulInfoFieldId>(["governmentType", "religion", "publicAffection", "lgbtq", "civilRights", "specialPrecautions", "drinkingWater", "requiredVaccines", "passport", "visa", "healthInsurance", "embassy", "consulates", "internationalDrivingPermit", "taxiApps", "foodDeliveryApps"]);

/** Show a short lead when the content is intentionally structured or when a summary + details model improves readability. */
function summarizeField(fieldId: UsefulInfoFieldId, value: string, description: string, expandable: boolean): { summary: string; details: string } {
  const clean = essentialUsefulInfoText(value);
  const detail = essentialUsefulInfoText(description);
  if (!expandable || !clean || isUsefulInfoNotRequired(clean)) return { summary: clean, details: detail };

  const valueParagraphs = clean.split(/\n{2,}|\r\n\r\n/).map((part) => part.trim()).filter(Boolean);
  const detailParagraphs = detail.split(/\n{2,}|\r\n\r\n/).map((part) => part.trim()).filter(Boolean);

  if (valueParagraphs.length > 1) {
    return { summary: valueParagraphs[0], details: [...valueParagraphs.slice(1), ...detailParagraphs].join("\n\n") };
  }

  if (detailParagraphs.length) return { summary: clean, details: detailParagraphs.join("\n\n") };

  return { summary: clean, details: "" };
}

const domesticNotRequiredFieldIds: UsefulInfoFieldId[] = [
  "embassy", "consulates", "requiredVaccines", "visa", "passport", "internationalDrivingPermit"
];

const fallbackCurrencies = [
  "AED", "AFN", "ALL", "AMD", "ANG", "AOA", "ARS", "AUD", "AWG", "AZN", "BAM", "BBD", "BDT", "BGN", "BHD", "BIF", "BMD", "BND", "BOB", "BRL", "BSD", "BTN", "BWP", "BYN", "BZD", "CAD", "CDF", "CHF", "CLP", "CNY", "COP", "CRC", "CUP", "CVE", "CZK", "DJF", "DKK", "DOP", "DZD", "EGP", "ERN", "ETB", "EUR", "FJD", "FKP", "GBP", "GEL", "GHS", "GIP", "GMD", "GNF", "GTQ", "GYD", "HKD", "HNL", "HTG", "HUF", "IDR", "ILS", "INR", "IQD", "IRR", "ISK", "JMD", "JOD", "JPY", "KES", "KGS", "KHR", "KMF", "KPW", "KRW", "KWD", "KYD", "KZT", "LAK", "LBP", "LKR", "LRD", "LSL", "LYD", "MAD", "MDL", "MGA", "MKD", "MMK", "MNT", "MOP", "MRU", "MUR", "MVR", "MWK", "MXN", "MYR", "MZN", "NAD", "NGN", "NIO", "NOK", "NPR", "NZD", "OMR", "PAB", "PEN", "PGK", "PHP", "PKR", "PLN", "PYG", "QAR", "RON", "RSD", "RUB", "RWF", "SAR", "SBD", "SCR", "SDG", "SEK", "SGD", "SHP", "SLE", "SOS", "SRD", "SSP", "STN", "SYP", "SZL", "THB", "TJS", "TMT", "TND", "TOP", "TRY", "TTD", "TWD", "TZS", "UAH", "UGX", "USD", "UYU", "UZS", "VES", "VND", "VUV", "WST", "XAF", "XCD", "XCG", "XOF", "XPF", "YER", "ZAR", "ZMW", "ZWG"
];

const excludedCurrencyCodes = new Set(["BOV", "CHE", "CHW", "CLF", "COU", "MXV", "USN", "UYI", "UYW", "XAG", "XAU", "XBA", "XBB", "XBC", "XBD", "XPD", "XPT", "XSU", "XTS", "XUA", "XXX"]);

function normalizeUsefulInfoLayoutPreference(value: unknown, _version: unknown, complete: boolean): UsefulInfoLayout {
  return complete && value === "4" ? "4" : "1";
}


export function UsefulInfoSection({ trip, syncVersion = 0 }: { trip: TripSummary; syncVersion?: number }) {
  const { user, profile } = useAuth();
  const { complete } = usePlan();
  const { t, language } = useI18n();
  const uid = user?.uid || "anonymous";
  const [info, setInfo] = useState<TripUsefulInfo>(() => loadTripUsefulInfo(uid, trip.id));
  const [draft, setDraft] = useState<TripUsefulInfo>(() => loadTripUsefulInfo(uid, trip.id));
  const [editing, setEditing] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const printRootRef = useRef<HTMLElement | null>(null);
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => { const timer = window.setInterval(() => setClock(new Date()), 60_000); return () => window.clearInterval(timer); }, []);
  const layout = normalizeUsefulInfoLayoutPreference(profile?.preferences.usefulInfoLayout, profile?.preferences.usefulInfoLayoutVersion, complete);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set([groups[0].key]));
  const [expandedFields, setExpandedFields] = useState<Set<UsefulInfoFieldId>>(() => new Set());
  const multiStops = useMemo(() => {
    const places = listTripPlaces(uid, trip.id).map((item): UsefulInfoDestinationCandidate => ({
      name: item.city || item.name, date: item.startDate, address: item.address,
      latitude: item.latitude, longitude: item.longitude
    }));
    const hotels = listTripHotels(uid, trip.id).map((item): UsefulInfoDestinationCandidate => ({
      name: item.city, date: item.checkIn, address: item.address,
      latitude: item.latitude, longitude: item.longitude
    }));
    const itinerary = listTripItinerary(uid, trip.id).flatMap((item): UsefulInfoDestinationCandidate[] => [
      { name: item.city, date: item.date, latitude: item.latitude, longitude: item.longitude },
      { name: item.endCity || "", date: item.endDate || item.date, latitude: item.endLatitude, longitude: item.endLongitude }
    ]);
    const flights = listBoardingPasses(uid, trip.id).map((item): UsefulInfoDestinationCandidate => ({
      name: item.destinationCity || "", date: item.departureDate
    }));
    return uniqueUsefulInfoDestinations(trip.destination, [...places, ...hotels, ...itinerary, ...flights]);
  }, [uid, trip.id, trip.destination, syncVersion]);
  const multiMode = multiStops.length > 1;
  const [multiFacts, setMultiFacts] = useState<UsefulInfoLocationFact[]>([]);
  const [multiBusy, setMultiBusy] = useState(false);

  useEffect(() => {
    let active = true;
    if (!multiMode) {
      setMultiFacts([]);
      setMultiBusy(false);
      return () => { active = false; };
    }
    setMultiBusy(true);
    setMultiFacts([]);
    void (async () => {
      // Network and country intelligence are cached by the existing resolvers.
      // Unknown places stay unknown: do not assume the country of the trip header.
      const results: UsefulInfoLocationFact[] = [];
      const requirements = new Map<string, ReturnType<typeof fetchUsefulInfoTravelRequirements>>();
      const emergency = new Map<string, ReturnType<typeof fetchUsefulInfoEmergencyNumbers>>();
      const rates = new Map<string, ReturnType<typeof fetchUsefulInfoExchangeRate>>();
      for (const location of multiStops) {
        if (!active) return;
        const fact: UsefulInfoLocationFact = {
          location, countryCode: "", values: {}, descriptions: {}, evidence: {}
        };
        try {
          const context = await buildUsefulInfoCountryContext(location.query, profile?.nationality || "", language);
          if (!active) return;
          if (context) {
            fact.countryCode = context.destinationCountryCode;
            fact.values = { ...context.stableValues, ...context.domesticNotRequiredValues };
            fact.descriptions = { ...context.approvedDescriptions };
            const reviewed = verifiedUsefulInfo(context.destinationCountryCode, context.nationalityCode, language);
            Object.assign(fact.values, reviewed.values);
            Object.assign(fact.descriptions, reviewed.descriptions);
            for (const field of usefulInfoFieldIds) {
              if (fact.values[field]) fact.evidence[field] = context.approvedValues[field] ? "approved" : "embedded";
            }
            for (const field of domesticNotRequiredFieldIds) {
              if (context.domesticNotRequiredValues[field]) fact.evidence[field] = "approved";
            }
            const code = context.destinationCountryCode;
            const key = `${code}:${context.nationalityCode}:${language}`;
            if (!requirements.has(key)) requirements.set(key, fetchUsefulInfoTravelRequirements(code, context.nationalityCode, language));
            const requirementsResult = await requirements.get(key);
            if (!active) return;
            if (requirementsResult) {
              for (const [field, value] of Object.entries(requirementsResult.values) as Array<[UsefulInfoFieldId, string]>) {
                if (!value || fact.values[field]) continue;
                fact.values[field] = value;
                fact.evidence[field] = "external";
              }
            }
            if (!fact.values.emergencyNumber) {
              if (!emergency.has(code)) emergency.set(code, fetchUsefulInfoEmergencyNumbers(code, {
                dispatch: t("usefulInfo.emergency.dispatch"), police: t("usefulInfo.emergency.police"),
                ambulance: t("usefulInfo.emergency.ambulance"), fire: t("usefulInfo.emergency.fire")
              }));
              const phone = await emergency.get(code);
              if (!active) return;
              if (phone) { fact.values.emergencyNumber = phone; fact.evidence.emergencyNumber = "external"; }
            }
            const reference = String(profile?.preferences.currency || "EUR").trim().toUpperCase();
            const currency = String(fact.values.currency || "").trim().toUpperCase();
            if (currency) {
              const pair = `${reference}:${currency}:${language}`;
              if (!rates.has(pair)) rates.set(pair, fetchUsefulInfoExchangeRate(reference, currency, language));
              const rate = await rates.get(pair);
              if (!active) return;
              if (rate?.value) { fact.values.exchangeRate = rate.value; fact.evidence.exchangeRate = "external"; }
            }
            const supportContacts = resolveSupportContacts(code, context.nationalityCode, language);
            for (const [field, supportValue] of Object.entries(supportContacts) as Array<[UsefulInfoFieldId, string]>) {
              if (shouldOverrideSupportContact(field, fact.values[field] || "")) {
                fact.values[field] = supportValue;
                fact.evidence[field] = "external";
              }
            }
            fact.timeZone = resolveDestinationTimeZone(code, location.name) || undefined;
          }
        } catch { /* Never manufacture a country, visa, or timezone on lookup failure. */ }
        results.push(fact);
      }
      if (active) { setMultiFacts(results); setMultiBusy(false); }
    })().catch(() => { if (active) setMultiBusy(false); });
    return () => { active = false; };
  }, [multiMode, multiStops, uid, trip.id, language, profile?.nationality, profile?.preferences.currency]);


  useEffect(() => {
    const remote = loadTripUsefulInfo(uid, trip.id);
    setInfo(remote);
    if (!editing) setDraft(remote);
  }, [syncVersion, uid, trip.id, editing]);

  useEffect(() => {
    let active = true;
    if (!trip.destination.trim() || multiMode || trip.archivedAt) return () => { active = false; };

    void (async () => {
      const context = await buildUsefulInfoCountryContext(trip.destination, profile?.nationality || "", language);
      if (!active || !context) return;

      const current = loadTripUsefulInfo(uid, trip.id);
      const enriched = applyVerifiedContext(applyCountryContext(current, context), context, language);
      const supportContacts = resolveSupportContacts(context.destinationCountryCode, context.nationalityCode, language);
      let prepared = enriched;
      if (Object.keys(supportContacts).length) {
        const nextValues = { ...enriched.values };
        let changedContacts = false;
        for (const [field, supportValue] of Object.entries(supportContacts) as Array<[UsefulInfoFieldId, string]>) {
          if (shouldOverrideSupportContact(field, nextValues[field] || "")) {
            nextValues[field] = supportValue;
            changedContacts = true;
          }
        }
        if (changedContacts) prepared = { ...enriched, values: nextValues };
      }
      let saved = prepared === current ? current : saveTripUsefulInfo(uid, trip.id, prepared);
      // Publish embedded essentials before waiting for optional remote enrichment.
      setInfo(saved);
      if (!editing) setDraft(saved);

      const destinationCurrency = String(context.stableValues.currency || "").trim().toUpperCase();
      const referenceCurrency = String(profile?.preferences.currency || "EUR").trim().toUpperCase();
      const [rateResult, emergencyValue, requirementsResult] = await Promise.all([
        fetchUsefulInfoExchangeRate(referenceCurrency, destinationCurrency, language),
        (context.approvedValues.emergencyNumber || verifiedUsefulInfo(context.destinationCountryCode, context.nationalityCode, language).values.emergencyNumber)
          ? Promise.resolve("")
          : fetchUsefulInfoEmergencyNumbers(context.destinationCountryCode, {
              dispatch: t("usefulInfo.emergency.dispatch"),
              police: t("usefulInfo.emergency.police"),
              ambulance: t("usefulInfo.emergency.ambulance"),
              fire: t("usefulInfo.emergency.fire")
            }),
        fetchUsefulInfoTravelRequirements(context.destinationCountryCode, context.nationalityCode, language)
      ]);
      if (!active) return;

      const latest = loadTripUsefulInfo(uid, trip.id);
      const nextValues = { ...latest.values };
      let changed = false;
      const now = new Date().toISOString();
      let autoExchangeRateValue = latest.autoExchangeRateValue;
      let autoExchangeRateUpdatedAt = latest.autoExchangeRateUpdatedAt;
      let autoEmergencyNumberValue = latest.autoEmergencyNumberValue;
      let autoEmergencyNumberUpdatedAt = latest.autoEmergencyNumberUpdatedAt;
      let autoRequirementsValues = { ...(latest.autoRequirementsValues || {}) };
      let autoRequirementsUpdatedAt = latest.autoRequirementsUpdatedAt;
      let autoRequirementsDatasetVersion = latest.autoRequirementsDatasetVersion;

      if (rateResult && (!nextValues.exchangeRate || nextValues.exchangeRate === latest.autoExchangeRateValue)) {
        if (nextValues.exchangeRate !== rateResult.value) changed = true;
        nextValues.exchangeRate = rateResult.value;
        autoExchangeRateValue = rateResult.value;
        autoExchangeRateUpdatedAt = rateResult.sourceDate || now;
      }

      if (emergencyValue && (!nextValues.emergencyNumber || nextValues.emergencyNumber === latest.autoEmergencyNumberValue)) {
        if (nextValues.emergencyNumber !== emergencyValue) changed = true;
        nextValues.emergencyNumber = emergencyValue;
        autoEmergencyNumberValue = emergencyValue;
        autoEmergencyNumberUpdatedAt = now;
      }

      if (requirementsResult) {
        for (const [field, value] of Object.entries(requirementsResult.values) as Array<[UsefulInfoFieldId, string]>) {
          if (!value || isUsefulInfoNotRequired(nextValues[field])) continue;
          const previousAutoValue = latest.autoRequirementsValues?.[field];
          if (!nextValues[field] || nextValues[field] === previousAutoValue) {
            if (nextValues[field] !== value) changed = true;
            nextValues[field] = value;
            autoRequirementsValues[field] = value;
          }
        }
        autoRequirementsUpdatedAt = requirementsResult.updatedAt || now;
        autoRequirementsDatasetVersion = requirementsResult.datasetVersion;
        if (
          latest.autoRequirementsUpdatedAt !== autoRequirementsUpdatedAt ||
          latest.autoRequirementsDatasetVersion !== autoRequirementsDatasetVersion
        ) changed = true;
      }

      if (changed) {
        saved = saveTripUsefulInfo(uid, trip.id, {
          ...latest,
          values: nextValues,
          source: latest.source === "manual" && !latest.updatedAt ? "database" : latest.source === "manual" ? "mixed" : latest.source,
          updatedAt: now,
          autoExchangeRateValue,
          autoExchangeRateUpdatedAt,
          autoEmergencyNumberValue,
          autoEmergencyNumberUpdatedAt,
          autoRequirementsValues,
          autoRequirementsUpdatedAt,
          autoRequirementsDatasetVersion
        });
      } else {
        saved = latest;
      }

      if (!active) return;
      setInfo(saved);
      if (!editing) setDraft(saved);
    })();

    return () => { active = false; };
  }, [syncVersion, uid, trip.id, trip.destination, trip.archivedAt, multiMode, profile?.nationality, profile?.preferences.currency, language, editing]);

  const currencies = useMemo(() => getSupportedCurrencies(), []);
  const filledCount = useMemo(
    () => usefulInfoFieldIds.filter((field) => Boolean(info.values[field]?.trim())).length,
    [info]
  );
  const completionPercent = Math.round((filledCount / usefulInfoFieldIds.length) * 100);

  function beginEdit() {
    if (trip.archivedAt || multiMode) return;
    setDraft({ ...info, values: { ...info.values }, descriptions: { ...info.descriptions } });
    setEditing(true);
    setMessage(null);
  }

  function cancelEdit() {
    setDraft({ ...info, values: { ...info.values }, descriptions: { ...info.descriptions } });
    setEditing(false);
    setMessage(null);
  }

  function updateField(field: UsefulInfoFieldId, value: string) {
    setDraft((current) => ({ ...current, values: { ...current.values, [field]: value } }));
  }

  function updateDescription(field: UsefulInfoFieldId, description: string) {
    setDraft((current) => ({ ...current, descriptions: { ...current.descriptions, [field]: description } }));
  }

  function togglePlugType(code: PlugTypeCode) {
    const selected = new Set(extractPlugCodes(draft.values.plugType));
    if (selected.has(code)) selected.delete(code);
    else selected.add(code);
    const ordered = plugTypes.filter((type) => selected.has(type));
    updateField("plugType", ordered.join(", "));
  }

  function saveManualChanges() {
    if (trip.archivedAt || multiMode) return;
    const now = new Date().toISOString();
    const next = saveTripUsefulInfo(uid, trip.id, {
      ...draft,
      source: info.source === "ai" || info.source === "database" || info.source === "mixed" ? "mixed" : "manual",
      updatedAt: now
    });
    setInfo(next);
    setDraft(next);
    setEditing(false);
    setMessage({ type: "success", text: t("usefulInfo.saved") });
  }

  async function generateWithAi() {
    if (!trip.destination.trim()) {
      setMessage({ type: "error", text: t("usefulInfo.destinationRequired") });
      return;
    }
    setAiBusy(true);
    setMessage(null);
    try {
      const context = await buildUsefulInfoCountryContext(trip.destination, profile?.nationality || "", language);
      const response = await askTripAI({
        capability: "suggestions",
        tripId: trip.id,
        input: buildAiPrompt(trip, profile?.nationality || "", language, context?.destinationCountryCode || "")
      });
      if (response.status !== "ok" || !response.output) throw new Error("AI_DISABLED");
      const generatedValues = parseAiValues(response.output);
      const now = new Date().toISOString();
      const protectedValues = context ? { ...context.stableValues, ...context.domesticNotRequiredValues } : {};
      const next = saveTripUsefulInfo(uid, trip.id, {
        values: { ...createEmptyUsefulInfo().values, ...generatedValues, ...protectedValues },
        source: context && Object.keys(protectedValues).length ? "mixed" : "ai",
        updatedAt: now,
        aiGeneratedAt: now,
        countryFactsVersion: context ? COUNTRY_FACTS_DATASET_VERSION : undefined,
        destinationCountryCode: context?.destinationCountryCode,
        nationalityCode: context?.nationalityCode
      });
      setInfo(next);
      setDraft(next);
      setEditing(false);
      setMessage({ type: "success", text: t("usefulInfo.aiCompleted") });
    } catch {
      setMessage({ type: "error", text: t("usefulInfo.aiError") });
    } finally {
      setAiBusy(false);
    }
  }

  async function exportPdf() {
    if (multiMode && (multiBusy || multiFacts.length !== multiStops.length)) {
      setMessage({ type: "error", text: t("usefulInfo.multi.loading") }); return;
    }
    try {
      const mapsLabel = usefulInfoMapsLabel(language);
      const pdfClock = (zone: string | null) => {
        const reading = zone ? formatDestinationTime(zone, clock, language, t("usefulInfo.homeTime")) : null;
        return reading ? [reading.delta, t("usefulInfo.homeTime"), reading.localTime].join("\n") : t("usefulInfo.timeZoneUnavailable");
      };
      const fieldItem = (id: UsefulInfoFieldId, label: string, raw: string, detail = ""): UsefulInfoPdfItem => {
        const value = essentialUsefulInfoText(raw);
        const description = essentialUsefulInfoText(detail);
        const sameCurrency = normalizeCurrencyCode(value) === String(profile?.preferences.currency || "EUR").toUpperCase();
        const rate = id === "currency" && !sameCurrency ? essentialUsefulInfoText(info.values.exchangeRate) : "";
        return { label, value: [displayUsefulInfoValue(id, value, t) || usefulInfoFallbackValue(id, t), rate].filter(Boolean).join(" · "),
          detail: description, links: usefulInfoMapLocations(id, value + "\n" + description).map(location => ({ label: mapsLabel, url: location.url })) };
      };
      const guide: UsefulInfoPdfItem[] = groups.flatMap(group => [
        { heading: t(group.key) },
        ...group.fields.flatMap(field => multiMode
          ? groupUsefulInfoField(field.id, multiFacts).map(entry => fieldItem(field.id, `${t(field.label)} (${entry.locations.map(loc => loc.name).join(", ")})`, entry.value, entry.description))
          : [fieldItem(field.id, t(field.label), info.values[field.id], info.descriptions?.[field.id])]),
        ...(group.key === "usefulInfo.group.basics" ? (multiMode
          ? multiTimeZoneGroups(multiFacts, trip, clock).map(item => ({ label: `${t("usefulInfo.field.timeZone")} (${item.locations.map(loc => loc.name).join(", ")})`, value: pdfClock(item.zone) }))
          : [{ label: t("usefulInfo.field.timeZone"), value: pdfClock(destinationZone) }]) : [])
      ]);
      const content = buildUsefulInfoPdf(trip.name || "Mundely", guide, t("trip.section.usefulInfo"), mapsLabel);
      const name = `Mundely_Info_Utili_${trip.id.replace(/[^a-z0-9_-]/gi,"_").slice(0,30)}.pdf`;
      if (Capacitor.isNativePlatform()) await saveBytesToUserDownloads(content, name, "application/pdf");
      else {
        const url = URL.createObjectURL(new Blob([new Uint8Array(content)], { type: "application/pdf" }));
        const link = document.createElement("a"); link.href = url; link.download = name;
        document.body.appendChild(link); link.click(); link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
      setMessage({ type: "success", text: t("usefulInfo.pdfSaved") });
    } catch { setMessage({ type: "error", text: t("usefulInfo.pdfError") }); }
  }

  function toggleField(id: UsefulInfoFieldId) {
    setExpandedFields((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleGroup(key: string) {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const groupedClocks = useMemo(() => multiTimeZoneGroups(multiFacts, trip, clock), [multiFacts, trip.startDate, trip.endDate, clock]);
  const destinationZone = resolveDestinationTimeZone(info.destinationCountryCode || "", trip.destination || "");
  const destinationClock = destinationZone ? formatDestinationTime(destinationZone, clock, language, t("usefulInfo.homeTime")) : null;
  const summaryIds = new Set<UsefulInfoFieldId>();

  return (
    <section ref={printRootRef} data-layout={layout} className={`${editing ? "useful-info-section is-editing" : "useful-info-section"} useful-info-layout-${layout} useful-info-guide-v2`}>
      <div className="useful-info-hero useful-info-hero-compact">
        <div className="useful-info-hero-main">
          <div className="useful-info-hero-copy">
            <span className="useful-info-hero-icon" aria-hidden="true">🧭</span>
            <div className="useful-info-hero-heading">
              <h2>{t("trip.section.usefulInfo")}</h2>
              <p>{t("usefulInfo.subtitle", { destination: trip.destination || t("trip.destinationPending") })}</p>
            </div>
          </div>
          <button className="useful-info-download-icon" type="button" disabled={multiMode && (multiBusy || multiFacts.length !== multiStops.length)} onClick={exportPdf} aria-label={t("usefulInfo.downloadPdf")} title={t("usefulInfo.downloadPdf")}>
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12m-4-4 4 4 4-4M4 16v4h16v-4"/></svg>
          </button>
        </div>
        <div className="useful-info-actions">
          {!editing && !trip.archivedAt && !multiMode && <button className="button secondary" type="button" onClick={beginEdit}>{t("usefulInfo.edit")}</button>}
          {editing && <button className="button secondary" type="button" onClick={cancelEdit}>{t("common.cancel")}</button>}
          {editing && <button className="button primary" type="button" onClick={saveManualChanges}>{t("common.save")}</button>}
        </div>
      </div>

      {message && <div className={message.type === "success" ? "useful-info-message success" : "useful-info-message error"}>{message.text}</div>}
      {multiMode && <div className="useful-info-multi-intro" role="status">
        <strong>{t("usefulInfo.multi.title", { count: String(multiStops.length) })}</strong>
        <span>{multiBusy || multiFacts.length !== multiStops.length ? t("usefulInfo.multi.loading") : t("usefulInfo.multi.description")}</span>
      </div>}

      <div className="useful-info-groups">
        {groups.map((group) => (
          <section className={`useful-info-group ${layout === "3" && !expandedGroups.has(group.key) ? "is-collapsed" : ""}`} key={group.key} data-group={group.key}>
            <button
              className="useful-info-group-header"
              type="button"
              onClick={() => layout === "3" && toggleGroup(group.key)}
              aria-expanded={layout === "3" ? expandedGroups.has(group.key) : true}
            >
              <span className="useful-info-group-ribbon" aria-hidden="true" />
              <span className="useful-info-group-icon">{group.icon}</span>
              <div>
                <h3>{t(group.key)}</h3>
                <p>{t(group.description)}</p>
              </div>
              {layout === "3" && <span className="useful-info-group-chevron" aria-hidden="true">{expandedGroups.has(group.key) ? "⌃" : "⌄"}</span>}
            </button>
            <div className="useful-info-grid">
              {group.key === "usefulInfo.group.assistance" && !editing && !multiMode && (() => {
                const contacts = parseEmergencyContacts(info.values.emergencyNumber || "", t);
                return contacts.length ? contacts.map((contact) => (
                  <article className={`useful-info-card field-emergencySplit ${contact.number === t("usefulInfo.notAvailable") ? "is-empty" : "is-filled"}`} key={contact.key}>
                    <div className="useful-info-card-head">
                      <span className="useful-info-card-icon" aria-hidden="true">{contact.icon}</span>
                      <strong>{contact.label}</strong>
                    </div>
                    <p className="useful-info-value useful-info-emergency-number">{contact.number}</p>
                  </article>
                )) : <article className="useful-info-card field-emergencySplit"><div className="useful-info-card-head"><span className="useful-info-card-icon" aria-hidden="true">🚨</span><strong>{t("usefulInfo.field.emergencyNumber")}</strong></div><p className="useful-info-value">{displayUsefulInfoValue("emergencyNumber", info.values.emergencyNumber || "", t) || t("usefulInfo.notAvailable")}</p></article>;
              })()}
              {(group.key === "usefulInfo.group.assistance" && !editing && !multiMode ? group.fields.filter((field) => field.id !== "emergencyNumber" && !summaryIds.has(field.id)) : group.fields.filter((field) => !summaryIds.has(field.id))).map((field) => {
                if (multiMode) return <MultiUsefulInfoCard key={field.id} field={field} facts={multiFacts} loading={multiBusy || multiFacts.length !== multiStops.length} t={t} />;
                const value = editing ? draft.values[field.id] : info.values[field.id];
                const expandedField = expandableInfoFields.has(field.id);
                const { summary, details } = summarizeField(field.id, value, info.descriptions?.[field.id] || "", expandedField);
                const canExpand = !editing && expandedField && Boolean(value?.trim()) && Boolean(details);
                const fieldClass = `useful-info-card field-${field.id}${value ? " is-filled" : " is-empty"}${expandedField ? " is-expandable" : ""}`;
                return (
                  <article className={fieldClass} key={field.id}>
                    <div className="useful-info-card-head">
                      <span className="useful-info-card-icon">{field.icon}</span>
                      <strong>{compactFieldLabel(field.id, t)}</strong>
                      {canExpand && (
                        <button
                          className="useful-info-detail-toggle"
                          type="button"
                          aria-label={`${t(expandedFields.has(field.id) ? "usefulInfo.details.hide" : "usefulInfo.details.show")}: ${t(field.label)}`}
                          title={t(expandedFields.has(field.id) ? "usefulInfo.details.hide" : "usefulInfo.details.show")}
                          aria-expanded={expandedFields.has(field.id)}
                          aria-controls={`useful-info-detail-${field.id}`}
                          onClick={() => toggleField(field.id)}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
                        </button>
                      )}
                    </div>
                    {renderFieldContent({
                      fieldId: field.id,
                      value: editing ? value : summary,
                      allValues: editing ? draft.values : info.values,
                      destinationCountryCode: (editing ? draft : info).destinationCountryCode,
                      editing,
                      language,
                      currencies,
                      homeCurrency: String(profile?.preferences.currency || "EUR").trim().toUpperCase(),
                      t,
                      onChange: updateField,
                      onTogglePlug: togglePlugType
                    })}
                    {editing && (expandableInfoFields.has(field.id) || Boolean(draft.descriptions?.[field.id])) && (
                      <label className="useful-info-detail-editor">
                        <span>{t("usefulInfo.details.label")}</span>
                        <textarea
                          className="input useful-info-input"
                          rows={3}
                          value={essentialUsefulInfoText(draft.descriptions?.[field.id])}
                          onChange={(event) => updateDescription(field.id, event.target.value)}
                        />
                      </label>
                    )}
                    {!editing && usefulInfoMapLocations(field.id, `${value}\n${info.descriptions?.[field.id] || ""}`).map(location => (
                      <a className="useful-info-maps-link" key={location.url} href={location.url} target="_blank" rel="noopener noreferrer">{usefulInfoMapsLabel(language)}</a>
                    ))}
                    {canExpand && expandedFields.has(field.id) && (
                      <div className="useful-info-detail-wrap">
                        <p id={`useful-info-detail-${field.id}`} className="useful-info-detail-body">{linkedInfoText(details)}</p>
                      </div>
                    )}
                  </article>
                );
              })}
              {group.key === "usefulInfo.group.basics" && (multiMode ? <article className="useful-info-card field-timeZone">
                <div className="useful-info-card-head"><span className="useful-info-card-icon" aria-hidden="true">🕒</span><strong>{t("usefulInfo.field.timeZone")}</strong></div>
                {multiBusy || multiFacts.length !== multiStops.length ? <p className="empty">{t("usefulInfo.multi.loading")}</p> : <div className="useful-info-multi-values">
                  {groupedClocks.map((entry, index) => (
                    <div className="useful-info-multi-value" key={`${entry.zone || "unknown"}-${index}`}>
                      <span className="useful-info-multi-locations">{entry.locations.map((loc) => loc.name).join(" · ")}</span>
                      {entry.zone ? <><div className="useful-info-time-zone">
                        <strong>{formatDestinationTime(entry.zone, clock, language, t("usefulInfo.homeTime"))?.delta || t("usefulInfo.timeZoneUnavailable")}</strong>
                        <small>{t("usefulInfo.homeTime")}</small>
                        <span>{formatDestinationTime(entry.zone, clock, language, t("usefulInfo.homeTime"))?.localTime || ""}</span>
                      </div></> : <p className="empty">{t("usefulInfo.timeZoneUnavailable")}</p>}
                    </div>
                  ))}
                </div>}
              </article> : <article className="useful-info-card is-filled field-timeZone">
                <div className="useful-info-card-head"><span className="useful-info-card-icon" aria-hidden="true">🕒</span><strong>{t("usefulInfo.field.timeZone")}</strong></div>
                {destinationClock ? <div className="useful-info-time-zone"><strong>{destinationClock.delta}</strong><small>{t("usefulInfo.homeTime")}</small><span>{destinationClock.localTime}</span></div> : <p className="useful-info-value">{t("usefulInfo.timeZoneUnavailable")}</p>}
              </article>)}
            </div>
          </section>
        ))}
      </div>

      <div className="useful-info-disclaimer">
        <span>{String.fromCharCode(9432)}</span>
        <p>{t("usefulInfo.disclaimer")}</p>
      </div>
    </section>
  );
}


interface GroupedTimeZone {
  zone: string;
  locations: UsefulInfoDestination[];
}

function multiTimeZoneGroups(facts: readonly UsefulInfoLocationFact[], trip: TripSummary, now: Date): GroupedTimeZone[] {
  const buckets = new Map<string, GroupedTimeZone>();
  const year = now.getUTCFullYear();
  // Compare rules at the travel dates as well as both seasons; equal current offsets alone are insufficient.
  const dates = [now, new Date(`${year}-01-15T12:00:00Z`), new Date(`${year}-07-15T12:00:00Z`),
    ...[trip.startDate, trip.endDate].filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date)).map((date) => new Date(`${date}T12:00:00Z`))];
  for (const fact of facts) {
    let signature = `unknown:${fact.location.id}`;
    const zone = fact.timeZone || "";
    if (zone) {
      try {
        signature = dates.map((date) => {
          const parts = new Intl.DateTimeFormat("en-GB", { timeZone: zone, timeZoneName: "longOffset" }).formatToParts(date);
          return parts.find((part) => part.type === "timeZoneName")?.value || "";
        }).join("|");
        if (!signature.replaceAll("|", "")) signature = `zone:${zone}`;
      } catch { signature = `unknown:${fact.location.id}`; }
    }
    const found = buckets.get(signature);
    if (found) found.locations.push(fact.location);
    else buckets.set(signature, { zone, locations: [fact.location] });
  }
  return [...buckets.values()];
}

function MultiUsefulInfoCard({ field, facts, loading, t }: {
  field: UsefulInfoGroup["fields"][number];
  facts: readonly UsefulInfoLocationFact[];
  loading: boolean;
  t: ReturnType<typeof useI18n>["t"];
}) {
  const readings = groupUsefulInfoField(field.id, facts);
  return <article className={`useful-info-card field-${field.id}`}>
    <div className="useful-info-card-head"><span className="useful-info-card-icon">{field.icon}</span><strong>{compactFieldLabel(field.id, t)}</strong></div>
    {loading ? <p className="empty">{t("usefulInfo.multi.loading")}</p> : <div className="useful-info-multi-values">
      {readings.map((group, index) => {
        const structured = summarizeField(field.id, group.value, group.description, expandableInfoFields.has(field.id));
        const displayValue = group.value ? displayUsefulInfoValue(field.id, structured.summary, t) : "";
        return <div className="useful-info-multi-value" key={`${field.id}-${index}`}>
          <span className="useful-info-multi-locations">{group.locations.map((loc) => loc.name).join(" · ")}</span>
          {group.value ? <p className="useful-info-value">{displayValue || usefulInfoFallbackValue(field.id, t)}</p> : <p className="empty">{usefulInfoFallbackValue(field.id, t)}</p>}
          {structured.details && <p className="useful-info-multi-detail">{linkedInfoText(structured.details)}</p>}
          {usefulInfoMapLocations(field.id, group.value + "\n" + group.description).map(location => <a className="useful-info-maps-link" key={location.url} href={location.url} target="_blank" rel="noopener noreferrer">{usefulInfoMapsLabel(document.documentElement.lang)}</a>)}

        </div>;
      })}
    </div>}
  </article>;
}

function applyCountryContext(current: TripUsefulInfo, context: UsefulInfoCountryContext): TripUsefulInfo {
  const nextValues = { ...current.values };
  const nextDescriptions = { ...current.descriptions };
  for (const field of ["embassy", "consulates"] as const) {
    if (/Wikidata|nessun[ao]? .*trovat|no .*found|no se encontr/i.test(nextValues[field] || "")) { nextValues[field] = ""; nextDescriptions[field] = ""; }
  }
  const destinationChanged = current.destinationCountryCode !== context.destinationCountryCode;
  const nationalityChanged = current.nationalityCode !== context.nationalityCode;
  const approvedVersion = context.approvedVersion;
  const cloudVersion = approvedVersion
    ? `${COUNTRY_FACTS_DATASET_VERSION}+approved:${approvedVersion}:${context.approvedLanguage || "it"}`
    : undefined;
  // If Firestore is unavailable while offline, retain the last approved snapshot.
  const effectiveVersion = cloudVersion || (
    !destinationChanged && current.countryFactsVersion?.startsWith(`${COUNTRY_FACTS_DATASET_VERSION}+approved:`)
      ? current.countryFactsVersion
      : COUNTRY_FACTS_DATASET_VERSION
  );
  const datasetChanged = current.countryFactsVersion !== effectiveVersion;

  if (destinationChanged || datasetChanged) {
    for (const field of countryFactFieldIds) {
      const prior = nextValues[field];
      const baseline = context.baseStableValues[field] || "";
      const lastApproved = current.approvedCountryValues?.[field];
      const isAutoManaged = destinationChanged || !prior || prior === baseline ||
        (lastApproved !== undefined && prior === lastApproved) ||
        (current.source === "database" && !current.approvedCountryValues);
      // Preserve independently edited trip values even when the global catalogue updates.
      if (isAutoManaged) nextValues[field] = context.stableValues[field] || "";
      const previousDescription = current.descriptions?.[field] || "";
      const priorApprovedDescription = current.approvedCountryDescriptions?.[field];
      const descriptionAutoManaged = destinationChanged || !previousDescription ||
        (priorApprovedDescription !== undefined && previousDescription === priorApprovedDescription);
      if (descriptionAutoManaged) nextDescriptions[field] = context.approvedDescriptions[field] || "";
    }
  }
  if (destinationChanged) {
    if (current.autoExchangeRateValue && nextValues.exchangeRate === current.autoExchangeRateValue) nextValues.exchangeRate = "";
    if (current.autoEmergencyNumberValue && nextValues.emergencyNumber === current.autoEmergencyNumberValue) nextValues.emergencyNumber = "";
  }
  if (destinationChanged || nationalityChanged) {
    for (const [field, value] of Object.entries(current.autoRequirementsValues || {}) as Array<[UsefulInfoFieldId, string]>) {
      if (value && nextValues[field] === value) nextValues[field] = "";
    }
  }

  const domestic = context.destinationCountryCode === context.nationalityCode && Boolean(context.nationalityCode);
  if (domestic) {
    for (const field of domesticNotRequiredFieldIds) nextValues[field] = USEFUL_INFO_NOT_REQUIRED;
  } else if (destinationChanged || nationalityChanged) {
    for (const field of domesticNotRequiredFieldIds) {
      if (isUsefulInfoNotRequired(nextValues[field])) nextValues[field] = "";
    }
  }

  const valuesChanged = usefulInfoFieldIds.some((field) => nextValues[field] !== current.values[field]);
  const descriptionsChanged = usefulInfoFieldIds.some((field) => (nextDescriptions[field] || "") !== (current.descriptions?.[field] || ""));
  const metadataChanged = destinationChanged || datasetChanged || nationalityChanged;
  if (!valuesChanged && !descriptionsChanged && !metadataChanged) return current;

  const hadContent = usefulInfoFieldIds.some((field) => Boolean(current.values[field]?.trim()));
  const source = !hadContent || current.source === "database"
    ? "database"
    : current.source === "manual" || current.source === "ai" || current.source === "mixed"
      ? "mixed"
      : current.source;

  return {
    ...current,
    values: nextValues,
    descriptions: nextDescriptions,
    source,
    updatedAt: new Date().toISOString(),
    countryFactsVersion: effectiveVersion,
    approvedCountryValues: context.approvedVersion
      ? { ...context.approvedValues }
      : destinationChanged ? undefined : current.approvedCountryValues,
    approvedCountryDescriptions: context.approvedVersion
      ? { ...context.approvedDescriptions }
      : destinationChanged ? undefined : current.approvedCountryDescriptions,
    destinationCountryCode: context.destinationCountryCode,
    nationalityCode: context.nationalityCode,
    autoExchangeRateValue: destinationChanged ? undefined : current.autoExchangeRateValue,
    autoExchangeRateUpdatedAt: destinationChanged ? undefined : current.autoExchangeRateUpdatedAt,
    autoEmergencyNumberValue: destinationChanged ? undefined : current.autoEmergencyNumberValue,
    autoEmergencyNumberUpdatedAt: destinationChanged ? undefined : current.autoEmergencyNumberUpdatedAt,
    autoRequirementsValues: destinationChanged || nationalityChanged ? undefined : current.autoRequirementsValues,
    autoRequirementsUpdatedAt: destinationChanged || nationalityChanged ? undefined : current.autoRequirementsUpdatedAt,
    autoRequirementsDatasetVersion: destinationChanged || nationalityChanged ? undefined : current.autoRequirementsDatasetVersion
  };
}

function displayUsefulInfoValue(
  fieldId: UsefulInfoFieldId,
  value: string,
  t: ReturnType<typeof useI18n>["t"]
): string {
  const raw = essentialUsefulInfoText(value);
  if (!raw) return "";
  if (isUsefulInfoNotRequired(raw)) return t("usefulInfo.notRequired");
  if (fieldId === "drivingSide") {
    if (raw.toLowerCase() === "left") return t("usefulInfo.driving.left");
    if (raw.toLowerCase() === "right") return t("usefulInfo.driving.right");
  }
  return raw;
}

function usefulInfoFallbackValue(fieldId: UsefulInfoFieldId, t: ReturnType<typeof useI18n>["t"]): string {
  if (fieldId === "healthInsurance") return t("usefulInfo.healthInsuranceHint");
  if (fieldId === "civilRights") return t("usefulInfo.civilRightsPending");
  if (fieldId === "religion") return t("usefulInfo.toBeCompleted");
  return t("usefulInfo.notAvailable");
}

function shouldOverrideSupportContact(fieldId: UsefulInfoFieldId, currentValue: string): boolean {
  const raw = String(currentValue || "").trim().toLowerCase();
  if (!raw) return true;
  if (fieldId === "embassy") return raw.startsWith("nessuna ambasciata trovata") || raw.startsWith("no embassy found");
  if (fieldId === "consulates") return raw.startsWith("nessun consolato trovato") || raw.startsWith("no consulate found") || raw.startsWith("no consulates found");
  return false;
}

function resolveSupportContacts(countryCode: string, nationalityCode: string, language: "it" | "en" | "es"): Partial<Record<UsefulInfoFieldId, string>> {
  const { values } = verifiedUsefulInfo(countryCode, nationalityCode, language);
  return { ...(values.embassy ? { embassy: values.embassy } : {}), ...(values.consulates ? { consulates: values.consulates } : {}) };
}

type EmergencyContact = { key: string; label: string; number: string; icon: string };
type EmergencyTranslator = ReturnType<typeof useI18n>["t"];

function parseEmergencyContacts(value: string, t: EmergencyTranslator): EmergencyContact[] {
  const raw = String(value || "").trim();
  if (!raw) return ["police", "ambulance", "fire"].map((key) => ({ key, label: t(`usefulInfo.emergency.${key}` as TranslationKey), icon: key === "police" ? "🚓" : key === "ambulance" ? "🚑" : "🚒", number: t("usefulInfo.notAvailable") }));
  const parts = raw.split(/[·•]/).map((part) => part.trim()).filter(Boolean);
  const aliases = [
    { key: "police", icon: "🚓", labels: ["polizia", "police", "policía"] },
    { key: "ambulance", icon: "🚑", labels: ["ambulanza", "ambulance", "ambulancia"] },
    { key: "fire", icon: "🚒", labels: ["vigili del fuoco", "fire", "bomberos"] }
  ] as const;
  const found = new Map<string, EmergencyContact>();
  for (const part of parts) {
    const lower = part.toLowerCase();
    const matches = aliases.filter((entry) => entry.labels.some((label) => lower.includes(label)));
    const number = (part.match(/\+?\d[\d\s().-]*/)?.[0] || "").trim();
    if (!number) continue;
    for (const match of matches) found.set(match.key, { key: match.key, label: t(`usefulInfo.emergency.${match.key}` as TranslationKey), number, icon: match.icon });
    if (/gener|dispatch|emergen|numero unico|número único/.test(lower) || lower.includes(t("usefulInfo.emergency.dispatch").toLowerCase()) || /^\+?\d[\d\s().-]*$/.test(part)) for (const match of aliases) { if (!found.has(match.key)) found.set(match.key, { key: match.key, label: t(`usefulInfo.emergency.${match.key}` as TranslationKey), number, icon: match.icon }); }
  }
  return aliases.map((entry) => found.get(entry.key) || { key: entry.key, label: t(`usefulInfo.emergency.${entry.key}` as TranslationKey), number: t("usefulInfo.notAvailable"), icon: entry.icon });
}

function getSummaryFieldIds(layout: UsefulInfoLayout): Set<UsefulInfoFieldId> {
  if (layout === "5") return new Set<UsefulInfoFieldId>(["plugType", "currency", "drinkingWater", "emergencyNumber", "visa", "requiredVaccines", "drivingSide"]);
  if (layout === "2") return new Set<UsefulInfoFieldId>(["plugType", "currency", "drinkingWater", "emergencyNumber"]);
  return new Set<UsefulInfoFieldId>();
}

function compactFieldLabel(fieldId: UsefulInfoFieldId, t: ReturnType<typeof useI18n>["t"]): string {
  const map: Partial<Record<UsefulInfoFieldId, TranslationKey>> = {
    requiredVaccines: "usefulInfo.field.requiredVaccines.short",
    healthInsurance: "usefulInfo.field.healthInsurance.short",
    publicAffection: "usefulInfo.field.publicAffection.short",
    specialPrecautions: "usefulInfo.field.specialPrecautions.short",
    internationalDrivingPermit: "usefulInfo.field.internationalDrivingPermit.short",
    foodDeliveryApps: "usefulInfo.field.foodDeliveryApps.short",
    taxiApps: "usefulInfo.field.taxiApps.short"
  };
  return map[fieldId] ? t(map[fieldId]!) : t(groups.flatMap((group) => group.fields).find((field) => field.id === fieldId)?.label || "usefulInfo.notAvailable");
}

function UsefulInfoQuickSummary({ info, layout, t, language }: {
  info: TripUsefulInfo;
  layout: UsefulInfoLayout;
  t: ReturnType<typeof useI18n>["t"];
  language: string;
}) {
  const ids: UsefulInfoFieldId[] = layout === "5"
    ? ["plugType", "currency", "drinkingWater", "emergencyNumber", "visa", "requiredVaccines", "drivingSide"]
    : ["plugType", "currency", "drinkingWater", "emergencyNumber"];
  const icons: Partial<Record<UsefulInfoFieldId, string>> = {
    plugType: "🔌", currency: "💰", drinkingWater: "💧", emergencyNumber: "☎️", visa: "🛂", requiredVaccines: "💉", drivingSide: "🚗"
  };
  return (
    <div className="useful-info-quick-summary">
      {ids.map((id) => {
        let value = displayUsefulInfoValue(id, info.values[id], t) || t("usefulInfo.notAvailable");
        if (id === "currency" && !isUsefulInfoNotRequired(info.values[id]) && normalizeCurrencyCode(value)) {
          value = `${currencySymbol(normalizeCurrencyCode(value), language)} ${normalizeCurrencyCode(value)}`;
          const rate = displayUsefulInfoValue("exchangeRate", info.values.exchangeRate, t);
          if (rate) value = `${value} · ${rate}`;
        }
        return <div className="useful-info-quick-item" key={id}><span>{icons[id]}</span><div><strong>{t(`usefulInfo.field.${id}` as TranslationKey)}</strong><b>{value}</b></div></div>;
      })}
    </div>
  );
}

function renderFieldContent(input: {
  fieldId: UsefulInfoFieldId;
  value: string;
  allValues: Record<UsefulInfoFieldId, string>;
  destinationCountryCode?: string;
  editing: boolean;
  language: string;
  currencies: string[];
  homeCurrency: string;
  t: ReturnType<typeof useI18n>["t"];
  onChange: (field: UsefulInfoFieldId, value: string) => void;
  onTogglePlug: (code: PlugTypeCode) => void;
}) {
  const { fieldId, value, allValues, editing, language, currencies, homeCurrency, t, onChange, onTogglePlug } = input;

  if (isUsefulInfoNotRequired(value)) {
    return <p className="useful-info-value is-not-required">{t("usefulInfo.notRequired")}</p>;
  }

  if (fieldId === "plugType") {
    const selected = extractPlugCodes(value);
    if (editing) {
      return (
        <div className="plug-picker-wrap">
          <p className="useful-info-control-hint">{t("usefulInfo.plug.help")}</p>
          <div className="plug-picker">
            {plugTypes.map((code) => {
              const active = selected.includes(code);
              return (
                <button
                  className={active ? "plug-option is-selected" : "plug-option"}
                  type="button"
                  key={code}
                  onClick={() => onTogglePlug(code)}
                  aria-pressed={active}
                  aria-label={t("usefulInfo.plug.typeLabel", { type: code })}
                >
                  <PlugIllustration code={code} />
                  <span>{t("usefulInfo.plug.typeLabel", { type: code })}</span>
                  {active && <b aria-hidden="true">{String.fromCharCode(10003)}</b>}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    if (selected.length) {
      // Country-specific priority only where reviewed; do not guess for other countries.
      const primary = input.destinationCountryCode === "IT" && selected.includes("L") ? "L" : selected.length === 1 ? selected[0] : undefined;
      const others = primary ? selected.filter((code) => code !== primary) : selected;
      const renderPlug = (code: PlugTypeCode) => <div className="plug-display" key={code}><PlugIllustration code={code}/><strong>{t("usefulInfo.plug.typeLabel", { type: code })}</strong></div>;
      return <div className="plug-priority-display">
        {primary && <div className="plug-display-list">{renderPlug(primary)}</div>}
        {others.length > 0 && (primary ? <details className="plug-other-types"><summary>{t("usefulInfo.plug.otherTypes")}</summary><div className="plug-display-list">{others.map(renderPlug)}</div></details> : <div className="plug-display-list">{others.map(renderPlug)}</div>)}
        <p className="useful-info-control-hint">{t("usefulInfo.plug.checkAdapter")}</p>
      </div>;
    }
    return <p className="empty">{t("usefulInfo.notAvailable")}</p>;
  }

  if (fieldId === "drivingSide" && !editing) {
    const side = displayUsefulInfoValue(fieldId, value, t);
    const isRight = String(value || "").trim().toLowerCase() === "right";
    const isLeft = String(value || "").trim().toLowerCase() === "left";
    const arrow = isRight ? "➡️" : isLeft ? "⬅️" : "🛣️";
    return (
      <div className="useful-info-driving-side">
        <span className="useful-info-driving-icon" aria-hidden="true">{arrow}</span>
        <strong>{side || t("usefulInfo.notAvailable")}</strong>
      </div>
    );
  }

  if (fieldId === "currency") {
    const exchangeRateValue = String(allValues.exchangeRate || "").trim();
    if (editing) {
      return (
        <div className="useful-info-currency-stack">
          <select className="input useful-info-select" value={normalizeCurrencyCode(value)} onChange={(event) => onChange(fieldId, event.target.value)}>
            <option value="">{t("usefulInfo.currency.select")}</option>
            {currencies.map((code) => <option key={code} value={code}>{formatCurrencyOption(code, language)}</option>)}
          </select>
          <label className="useful-info-inline-field">
            <span>{t("usefulInfo.field.exchangeRate")}</span>
            <input
              className="input useful-info-text-input"
              value={exchangeRateValue}
              onChange={(event) => onChange("exchangeRate", event.target.value)}
              placeholder={t("usefulInfo.editPlaceholder")}
            />
          </label>
        </div>
      );
    }
    const code = normalizeCurrencyCode(value);
    const normalizedRate = exchangeRateValue.toUpperCase().replace(/\s+/g, "");
    const sameCurrency = !!code && code === String(homeCurrency || "").trim().toUpperCase();
    const sameRate = !!code && normalizedRate === `1${code}=1${code}`;
    if (code) {
      return (
        <div className="useful-info-currency-stack">
          <div className="currency-display">
            <span className="currency-display-symbol">{currencySymbol(code, language)}</span>
            <div>
              <strong>{code}</strong>
              <span>{currencyName(code, language)}</span>
            </div>
          </div>
          {!sameCurrency && !sameRate && exchangeRateValue && <p className="useful-info-exchange-line">{exchangeRateValue}</p>}
        </div>
      );
    }
    return <p className="empty">{value || t("usefulInfo.notAvailable")}</p>;
  }

  if (editing) {
    if (shortFieldIds.has(fieldId)) {
      return (
        <input
          className="input useful-info-text-input"
          value={value}
          onChange={(event) => onChange(fieldId, event.target.value)}
          placeholder={t("usefulInfo.editPlaceholder")}
        />
      );
    }
    return (
      <textarea
        className="input useful-info-input"
        value={value}
        onChange={(event) => onChange(fieldId, event.target.value)}
        placeholder={t("usefulInfo.editPlaceholder")}
        rows={3}
      />
    );
  }

  const displayValue = displayUsefulInfoValue(fieldId, value, t);
  const advice = !value ? usefulInfoFallbackValue(fieldId, t) : "";
  return <p className={value || advice ? "useful-info-value" : "empty"}>{linkedInfoText(displayValue || advice)}</p>;
}

function PlugIllustration({ code }: { code: PlugTypeCode }) {
  const common = { fill: "currentColor" };
  const hole = (cx: number, cy: number, r = 5) => <circle cx={cx} cy={cy} r={r} {...common} />;
  const slot = (x: number, y: number, width = 7, height = 18, rotate = 0) => (
    <rect x={x} y={y} width={width} height={height} rx={3} transform={rotate ? `rotate(${rotate} ${x + width / 2} ${y + height / 2})` : undefined} {...common} />
  );

  let pins;
  switch (code) {
    case "A": pins = <>{slot(28, 19)}{slot(51, 19)}</>; break;
    case "B": pins = <>{slot(28, 17)}{slot(51, 17)}{hole(43, 44, 5)}</>; break;
    case "C": pins = <>{hole(31, 30)}{hole(55, 30)}</>; break;
    case "D": pins = <>{hole(43, 15)}{hole(29, 40)}{hole(57, 40)}</>; break;
    case "E": pins = <>{hole(31, 34)}{hole(55, 34)}{hole(43, 14, 4)}</>; break;
    case "F": pins = <>{hole(31, 30)}{hole(55, 30)}{slot(14, 24, 4, 12)}{slot(68, 24, 4, 12)}</>; break;
    case "G": pins = <>{slot(39, 10, 8, 17)}{slot(25, 35, 8, 17, -90)}{slot(53, 35, 8, 17, -90)}</>; break;
    case "H": pins = <>{slot(38, 10, 7, 18)}{slot(27, 35, 7, 18, -25)}{slot(52, 35, 7, 18, 25)}</>; break;
    case "I": pins = <>{slot(39, 9, 7, 17)}{slot(28, 32, 7, 18, -35)}{slot(51, 32, 7, 18, 35)}</>; break;
    case "J": pins = <>{hole(43, 15, 4)}{hole(31, 38, 4)}{hole(55, 38, 4)}</>; break;
    case "K": pins = <>{hole(31, 26, 4)}{hole(55, 26, 4)}{hole(43, 44, 4)}</>; break;
    case "L": pins = <>{hole(25, 30, 4)}{hole(43, 30, 4)}{hole(61, 30, 4)}</>; break;
    case "M": pins = <>{hole(43, 13, 6)}{hole(27, 42, 6)}{hole(59, 42, 6)}</>; break;
    case "N": pins = <>{hole(43, 16, 4)}{hole(32, 38, 4)}{hole(54, 38, 4)}</>; break;
    case "O": pins = <>{hole(43, 14, 4)}{hole(28, 39, 5)}{hole(58, 39, 5)}</>; break;
  }

  return (
    <svg className="plug-illustration" viewBox="0 0 86 58" aria-hidden="true" focusable="false">
      <rect x="2" y="2" width="82" height="54" rx="16" fill="none" stroke="currentColor" strokeWidth="2" opacity=".16" />
      {pins}
    </svg>
  );
}

function extractPlugCodes(value: string): PlugTypeCode[] {
  const matches = String(value || "").toUpperCase().match(/\b[A-O]\b/g) || [];
  const selected = new Set(matches.filter((code): code is PlugTypeCode => plugTypes.includes(code as PlugTypeCode)));
  return plugTypes.filter((code) => selected.has(code));
}

function getSupportedCurrencies(): string[] {
  try {
    const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    const values = supportedValuesOf ? supportedValuesOf("currency") : fallbackCurrencies;
    const filtered = values.filter((code) => /^[A-Z]{3}$/.test(code) && !excludedCurrencyCodes.has(code));
    return [...new Set(filtered)].sort((a, b) => a.localeCompare(b));
  } catch {
    return fallbackCurrencies;
  }
}

function normalizeCurrencyCode(value: string): string {
  const upper = String(value || "").trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(upper)) return upper;
  const match = upper.match(/\b[A-Z]{3}\b/);
  return match ? match[0] : "";
}

function localeForLanguage(language: string): string {
  return language === "it" ? "it-IT" : language === "es" ? "es-ES" : "en-GB";
}

function currencyName(code: string, language: string): string {
  try {
    const DisplayNamesCtor = (Intl as unknown as {
      DisplayNames?: new (locales?: string | string[], options?: { type: "currency" }) => { of: (value: string) => string | undefined };
    }).DisplayNames;
    if (DisplayNamesCtor) return new DisplayNamesCtor(localeForLanguage(language), { type: "currency" }).of(code) || code;
  } catch {
    // Fall back to the code below.
  }
  return code;
}

function currencySymbol(code: string, language: string): string {
  try {
    const parts = new Intl.NumberFormat(localeForLanguage(language), {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).formatToParts(0);
    return parts.find((part) => part.type === "currency")?.value || code;
  } catch {
    return code;
  }
}

function formatCurrencyOption(code: string, language: string): string {
  const name = currencyName(code, language);
  const symbol = currencySymbol(code, language);
  return symbol === code ? `${code} — ${name}` : `${symbol}  ${code} — ${name}`;
}

function buildAiPrompt(trip: TripSummary, nationality: string, language: string, destinationCountryCode: string): string {
  const keys = usefulInfoFieldIds.join(", ");
  return [
    "Prepare practical travel information for a Mundely trip.",
    `Destination: ${trip.destination}.`,
    `Destination country code: ${destinationCountryCode || "unknown"}.`,
    `Travel dates: ${trip.startDate || "unknown"} to ${trip.endDate || "unknown"}.`,
    `Traveller nationality from the Mundely account: ${nationality || "not provided"}.`,
    `Output language: ${language}.`,
    "Return ONLY one valid JSON object, without markdown or commentary.",
    `The object must contain exactly these string keys: ${keys}.`,
    `When a requirement or service is genuinely not necessary for this traveller, return exactly ${USEFUL_INFO_NOT_REQUIRED}.`,
    "Decide whether something is not necessary from the traveller nationality stored in the account plus destination and dates. Never infer nationality from app language, residence or departure city.",
    "Keep each value concise but useful. For visa, passport, vaccines, embassy/consulate and legal/cultural rules, explicitly note when official verification is required.",
    "plugType must contain ONLY the applicable IEC plug letters separated by comma and space, for example: A, B, C.",
    "currency must contain ONLY the ISO 4217 three-letter currency code, for example: PHP.",
    "exchangeRate must describe the local currency versus a common reference currency and note that the live rate can change.",
    "drivingSide must say left or right and internationalDrivingPermit must explain whether an international permit is generally required/recommended for the stated nationality when known."
  ].join("\n");
}

function parseAiValues(output: string): Partial<Record<UsefulInfoFieldId, string>> {
  const start = output.indexOf("{");
  const end = output.lastIndexOf("}");
  if (start < 0 || end <= start) throw new Error("INVALID_AI_JSON");
  const parsed = JSON.parse(output.slice(start, end + 1)) as Record<string, unknown>;
  const result: Partial<Record<UsefulInfoFieldId, string>> = {};
  for (const field of usefulInfoFieldIds) {
    const value = parsed[field];
    if (typeof value === "string") result[field] = value.trim();
  }
  if (result.plugType && !isUsefulInfoNotRequired(result.plugType)) result.plugType = extractPlugCodes(result.plugType).join(", ");
  if (result.currency && !isUsefulInfoNotRequired(result.currency)) result.currency = normalizeCurrencyCode(result.currency);
  if (Object.keys(result).length < Math.ceil(usefulInfoFieldIds.length / 2)) throw new Error("INCOMPLETE_AI_JSON");
  return result;
}

function linkedInfoText(text: string) {
  return essentialUsefulInfoText(text).split(/(https:\/\/[^\s]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|\+\d[\d ()-]{6,}\d)/gi).map((part, i) => {
    const href = part.startsWith("https://") ? part : part.includes("@") ? "mailto:" + part : /^\+\d/.test(part) ? "tel:" + part.replace(/[^+\d]/g, "") : "";
    return href ? <a key={i} href={href} target={part.startsWith("https://") ? "_blank" : undefined} rel="noopener noreferrer">{part.startsWith("https://") ? new URL(part).hostname : part}</a> : part;
  });
}

function applyVerifiedContext(current: TripUsefulInfo, context: UsefulInfoCountryContext, language: "it" | "en" | "es"): TripUsefulInfo {
  const reviewed = verifiedUsefulInfo(context.destinationCountryCode, context.nationalityCode, language);
  const values = { ...current.values }, descriptions = { ...current.descriptions };
  let changed = false;
  for (const [field, value] of Object.entries(reviewed.values) as Array<[UsefulInfoFieldId, string]>) {
    const old = values[field] || "";
    const legacy = /Wikidata|Human Dignity Trust|snapshot|Da compilare|Contenuto in aggiornamento|Requisito da verificare|Convenzione rilevante|Nessuna ambasciata|Nessun consolato|No embassy|No consulate|Non risulta una regola globale|Destinazione generalmente sicura|repubblica parlamentare federale \/|Ungargasse 43|Rennweg 27/.test(old);
    const managed = !old || (legacy && current.source !== "manual") || current.source === "database" || old === current.autoRequirementsValues?.[field];
    if (!managed) continue;
    if (old !== value || descriptions[field] !== reviewed.descriptions[field]) changed = true;
    values[field] = value;
    descriptions[field] = reviewed.descriptions[field] || "";
  }
  return changed ? { ...current, values, descriptions, autoRequirementsValues: { ...current.autoRequirementsValues, ...Object.fromEntries(Object.entries(reviewed.values).filter(([field]) => values[field as UsefulInfoFieldId] === reviewed.values[field as UsefulInfoFieldId])) } } : current;
}

export { groups as usefulInfoGroups };
